/**
 * How an instant on the spine is written.
 *
 * Web and native both draw the same timeline, and both need the same answer to
 * "what does this row say next to it". Two copies of that arithmetic drift —
 * one rounds a 90-second gap to `1m ago` and the other to `2m ago`, and the
 * same event reads differently depending on which screen the user is looking
 * at. So the decision lives here once, and the surfaces render it.
 *
 * Formatting a *clock time* stays with the caller: that needs `Intl` and a
 * timezone preference, which is a rendering concern with a platform-specific
 * answer. What is shared is the part that is a judgement — when a relative
 * stamp stops being useful and a wall-clock time takes over.
 */

/**
 * How long until something scheduled, at the resolution a trader reads.
 *
 * Named for the spine because `widget/face.ts` already exports a `countdown`
 * that takes SECONDS and writes "in 3 h 10 m" for a widget face. Two functions
 * with one name and different units is the drift this module exists to stop.
 */
export function spineCountdown(ms: number): string {
  if (ms <= 0) return 'now';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3_600);
  const m = Math.floor((s % 3_600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${String(m).padStart(2, '0')}m`;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(sec).padStart(2, '0')}s`;
  return `${m}m ${String(sec).padStart(2, '0')}s`;
}

/**
 * What a published entry says, or `null` when it is old enough to want a clock
 * time instead.
 *
 * The twelve-hour boundary is the point where "14h ago" stops being easier to
 * read than "07:30" — past roughly half a day, a trader is thinking in session
 * times rather than in elapsed minutes. Returning `null` rather than a
 * formatted time keeps `Intl` and the timezone preference out of core.
 */
export function agoLabel(agoMs: number): string | null {
  if (agoMs < 60 * 60_000) return `${Math.max(1, Math.round(agoMs / 60_000))}m ago`;
  if (agoMs < 12 * 60 * 60_000) return `${Math.round(agoMs / 3_600_000)}h ago`;
  return null;
}

/**
 * A reference period (`2026-08`) as a month name.
 *
 * Built in UTC deliberately. `new Date(2026, 7, 1)` is local midnight, which is
 * the previous month for anyone west of Greenwich.
 *
 * Mutation testing showed that swapping `Date.UTC` for the local constructor
 * changes no output *today*, because `toLocaleString` is also passed
 * `timeZone: 'UTC'` and formats the resulting instant back into UTC — the two
 * errors cancel. The mutant is equivalent, not an untested gap. The pairing is
 * kept because it stops depending on that cancellation: drop the formatter's
 * `timeZone` for any reason and the local constructor starts mislabelling
 * prints, silently and only for some readers.
 */
export function periodLabel(period: string | null): string {
  if (!period) return '';
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return period;
  return new Date(Date.UTC(year, month - 1, 1))
    .toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}
